import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { patternTutorialEntries } from './patternTutorialData';

const biasMeta = {
  ALL: { label: 'Tous', color: '#19323c', background: '#f4ede2' },
  BULLISH: { label: 'Haussier', color: '#155d3b', background: '#dff3e5' },
  BEARISH: { label: 'Baissier', color: '#8d2f2f', background: '#fde2dc' },
  NEUTRAL: { label: 'Neutre', color: '#8a6a16', background: '#f8efc8' },
};

const reliabilityMeta = {
  High: { color: '#155d3b', background: '#dff3e5' },
  'Medium-High': { color: '#2f5d62', background: '#dcecf0' },
  Medium: { color: '#8a6a16', background: '#f8efc8' },
};

const reliabilityLabelByKey = {
  High: 'elevee',
  'Medium-High': 'moyenne a elevee',
  Medium: 'moyenne',
};

function buildPolylinePoints(schema) {
  return schema
    .map((value, index) => {
      const x = schema.length === 1 ? 0 : (index / (schema.length - 1)) * 100;
      return `${x},${value}`;
    })
    .join(' ');
}

function PatternSchema({ entry }) {
  const points = buildPolylinePoints(entry.schema);
  const tint = biasMeta[entry.bias] || biasMeta.NEUTRAL;
  return (
    <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(25,50,60,0.08)', background: 'linear-gradient(180deg, #fffdf8 0%, #f5eee2 100%)' }}>
      <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid rgba(25,50,60,0.08)' }}>
        <Typography variant="caption" sx={{ color: '#53646d', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Schema simplifie
        </Typography>
      </Box>
      <Box component="svg" viewBox="0 0 100 60" sx={{ width: '100%', display: 'block' }}>
        <defs>
          <linearGradient id={`gradient-${entry.key}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={tint.background} stopOpacity="0.95" />
            <stop offset="100%" stopColor="#fffaf2" stopOpacity="0.65" />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="100" height="60" fill={`url(#gradient-${entry.key})`} />
        <line x1="0" y1="52" x2="100" y2="52" stroke="#d7d0c2" strokeDasharray="2 2" />
        <polyline fill="none" stroke={tint.color} strokeWidth="3.2" strokeLinejoin="round" strokeLinecap="round" points={points} />
      </Box>
    </Box>
  );
}

function PatternTutorialCard({ entry, highlighted }) {
  const reliability = reliabilityMeta[entry.reliability] || reliabilityMeta.Medium;
  const bias = biasMeta[entry.bias] || biasMeta.NEUTRAL;
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: highlighted ? '1px solid rgba(18,44,52,0.28)' : '1px solid rgba(18,44,52,0.08)',
        background: highlighted ? 'linear-gradient(180deg, #fffaf2 0%, #f6efe2 100%)' : '#fff',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
        <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
          <PatternSchema entry={entry} />
        </Box>
        <Stack spacing={1.25} sx={{ flex: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#19323c' }}>
                {entry.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5d6d74' }}>
                {entry.exampleTitle}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {highlighted && <Chip size="small" label="Observe actuellement" sx={{ bgcolor: '#19323c', color: '#fff', fontWeight: 700 }} />}
              <Chip size="small" label={bias.label} sx={{ bgcolor: bias.background, color: bias.color, fontWeight: 700 }} />
              <Chip size="small" label={`Fiabilite : ${reliabilityLabelByKey[entry.reliability] || entry.reliability}`} sx={{ bgcolor: reliability.background, color: reliability.color, fontWeight: 700 }} />
            </Stack>
          </Stack>

          <InfoBlock label="Exemple reel" text={entry.exampleReal} />
          <InfoBlock label="Contexte haussier/baissier" text={entry.context} />
          <Box>
            <Typography variant="overline" sx={{ color: '#6a7a81', letterSpacing: '0.09em' }}>
              Pieges frequents
            </Typography>
            <Stack spacing={0.65} sx={{ mt: 0.5 }}>
              {entry.traps.map((trap) => (
                <Typography key={trap} variant="body2" sx={{ color: '#30454d', lineHeight: 1.55 }}>
                  • {trap}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Paper>
  );
}

function InfoBlock({ label, text }) {
  return (
    <Box>
      <Typography variant="overline" sx={{ color: '#6a7a81', letterSpacing: '0.09em' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#30454d', lineHeight: 1.65 }}>
        {text}
      </Typography>
    </Box>
  );
}

const PatternLearningDrawer = ({ open, onClose, highlightedPattern }) => {
  const [biasFilter, setBiasFilter] = useState('ALL');

  const entries = useMemo(() => {
    if (biasFilter === 'ALL') {
      return patternTutorialEntries;
    }
    return patternTutorialEntries.filter((entry) => entry.bias === biasFilter);
  }, [biasFilter]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', md: 780 }, maxWidth: '100vw', height: '100%', background: '#f7f3ea' }}>
        <Stack spacing={2.5} sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box>
              <Typography variant="overline" sx={{ color: '#6b7d86', letterSpacing: '0.12em' }}>
                Apprendre les figures chartistes
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#19323c' }}>
                Guide des patterns
              </Typography>
              <Typography variant="body2" sx={{ color: '#52636c', maxWidth: 560 }}>
                Fiches rapides pour comprendre la structure, le contexte, les pieges et le niveau de fiabilite des principales figures chartistes du scanner.
              </Typography>
            </Box>
            <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 99 }}>
              Fermer
            </Button>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(biasMeta).map(([key, meta]) => (
                <Chip
                  key={key}
                  label={meta.label}
                  onClick={() => setBiasFilter(key)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: biasFilter === key ? '#19323c' : meta.background,
                    color: biasFilter === key ? '#fff' : meta.color,
                    fontWeight: 700,
                  }}
                />
              ))}
            </Stack>
            <Chip
              label={`${entries.length} figure${entries.length > 1 ? 's' : ''}`}
              sx={{ bgcolor: '#efe7da', color: '#19323c', fontWeight: 700 }}
            />
          </Stack>

          <Divider />

          <Stack spacing={2} sx={{ pb: 2 }}>
            {entries.map((entry) => (
              <PatternTutorialCard
                key={entry.key}
                entry={entry}
                highlighted={entry.key === highlightedPattern}
              />
            ))}
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default PatternLearningDrawer;